import { NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import * as CryptoJS from 'crypto-js';
import { BodyZoomService } from './services/body-zoom.service';
import { UserService } from './services/user.service';

@Component({
  selector: 'app-root',
  imports: [NgIf, RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit {
  /**
   * Marks that the splash has already played in this browser session. It lives
   * in sessionStorage so it survives a reload but not a new tab or a new visit.
   */
  private static readonly SPLASH_SHOWN_KEY = 'helpdeskSplashShown';

  title = 'angular-helpdesk';
  isSessionOngoing: boolean = false;
  /**
   * The splash is a first-impression animation, not a progress bar: it plays
   * once when the app is first opened and is skipped on every reload and
   * in-app navigation afterwards, when the session is already in sessionStorage
   * and the page can render straight away.
   */
  showLoader: boolean = !sessionStorage.getItem(AppComponent.SPLASH_SHOWN_KEY);
  quickComplete: boolean = false;
  initialAnimationComplete: boolean = false;

  /**
   * Takes the splash down and records that it has played, so a reload goes
   * straight to the page.
   * @returns Nothing.
   */
  private dismissLoader(): void {
    this.showLoader = false;
    sessionStorage.setItem(AppComponent.SPLASH_SHOWN_KEY, '1');
  }

  private updateSessionOngoing(): void {
    const wasSessionOngoing = this.isSessionOngoing;
    this.isSessionOngoing = Boolean(
      sessionStorage.getItem('auth_token') &&
      sessionStorage.getItem('userName') &&
      sessionStorage.getItem('userID') && 
      sessionStorage.getItem('emailAddress')
    );
    console.log('[AppComponent] Session ongoing updated:', this.isSessionOngoing);
    
    // If session just became active, trigger completion
    if (!wasSessionOngoing && this.isSessionOngoing && this.showLoader) {
      this.handleSessionActive();
    }
  }



  private handleSessionActive(): void {
    if (this.initialAnimationComplete) {
      // If 60% animation is done, immediately start quick completion
      this.quickComplete = true;
      setTimeout(() => {
        this.dismissLoader();
      }, 500);
    }
    // If 60% animation is not done yet, the timeout below will handle it
  }



  private startInitialAnimation(): void {
    // Mark when the initial 60% animation completes
    setTimeout(() => {
      this.initialAnimationComplete = true;
      // If session is already active, start quick completion
      if (this.isSessionOngoing) {
        this.quickComplete = true;
        setTimeout(() => {
          this.dismissLoader();
        }, 500);
      }
    }, 3000); // 3 seconds for initial animation to 60%
  }

  encryptEmail(): void {
    let iv = CryptoJS.enc.Utf8.parse('1234567890123456');
    let key = CryptoJS.enc.Utf8.parse('1234567890123456');

    const email = window.sessionStorage.getItem('emailAddress');
    if (email) {
      window.sessionStorage.setItem('ecryptedEMail', CryptoJS.AES.encrypt(email, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }).toString());
    } else {
      console.warn('No emailAddress found in sessionStorage to encrypt.');
    }
  }

  /**
   * @param user - Authenticates the session and loads the signed-in user's data.
   * @param bodyZoom - Owner of the shell body zoom (0.8 on >=1024px viewports).
   */
  constructor(
    private readonly user: UserService,
    private readonly bodyZoom: BodyZoomService,
  ) { }

  ngOnInit(): void {

    // Hand body-zoom ownership to the service: initial apply for this viewport, then
    // re-evaluation on every crossing of the 1024px band.
    this.bodyZoom.init();

    const hasOpener = !!window.opener;
    const isFramed = window.self !== window.top;

    if (!hasOpener && !isFramed) {
      // Site B opened directly → no message will ever come
      console.warn('[AppComponent] Opened directly. Redirecting to login.');

      const currentURL = window.location.hostname;

      if (currentURL === 'helpdesk.teamleaseregtech.com') {
        window.location.href = 'https://login.teamleaseregtech.com/';
        return;
      } else if (currentURL === 'helpdesk.avantisregtec.in') {
        window.location.href = 'https://uat.avantisregtec.in/';
        return;
      } else if (currentURL === 'demohelpdesk.teamleaseregtech.com') {
        window.location.href = 'https://demo.teamleaseregtech.com/';
        return;
      } else if (currentURL === 'regtrackapp.avantisregtec.in') {
        window.location.href = 'https://uat.avantisregtec.in/';
        return;
      }

      return;
    }

    console.log('AppComponent initialized');

    const domainMap: { [key: string]: string } = {
      'https://login.teamleaseregtech.com': 'L',
      'https://demo.teamleaseregtech.com': 'D',
      'https://uat.avantisregtec.in': 'U',
      'https://impl.teamleaseregtech.com': 'U',
      'https://regtrackapp.avantisregtec.in': 'U',
      'https://compliance.teamleaseregtech.com': 'L',
      'https://democomplianceapp.teamleaseregtech.com': 'D'
    };

    const allowedOrigins = [
      'https://uat.avantisregtec.in',
      'https://login.teamleaseregtech.com',
      'https://demo.teamleaseregtech.com',
      'https://impl.teamleaseregtech.com',
      'https://regtrackapp.avantisregtec.in',
      'https://democomplianceapp.teamleaseregtech.com',
      'https://compliance.teamleaseregtech.com'
    ];

    this.updateSessionOngoing();
    this.startInitialAnimation(); // Start the initial animation timer

    if (!this.isSessionOngoing) {

      const messageHandler = (event: MessageEvent) => {

        const origin = event.origin;
        const domainCode = domainMap[origin];

        console.log('Detected origin:', event.origin);

        console.log('Mapped domainCode:', domainCode);

        if (domainCode) {
          sessionStorage.setItem("Domain", domainCode);
          console.log(`[AppComponent] Set sessionStorage Domain to: ${domainCode}`);
        } else {
          console.warn('Unknown domain:', origin);
          sessionStorage.setItem("Domain", 'LO');
          console.log('[AppComponent] Set sessionStorage Domain to fallback: LO');
        }


        if (!allowedOrigins.includes(event.origin)) {
          console.warn('[AppComponent] Blocked message from untrusted origin:', event.origin);
          return;
        }
        if (!event.data || !event.data.email_id) {
          console.warn('[AppComponent] Message missing email_id:', event.data);
          return;
        }
        console.log('[AppComponent] Accepted message from allowed origin:', event.origin);

        window.removeEventListener('message', messageHandler);

        this.user.authenticateAndStoreUserData(event.data.email_id).then(() => {
          this.user.validateUser().subscribe({
            next: (response) => {
              this.encryptEmail();
              sessionStorage.setItem('auth_token', response.access_token);
              // Use updateSessionOngoing to properly handle session state change
              this.updateSessionOngoing();
            },
            error: (error) => {
              console.error('[AppComponent] Validation failed:', error);
            }
          });
        }).catch((error) => {
          console.error('[AppComponent] Authentication or user data fetch failed:', error)
        });
      };

      window.addEventListener('message', messageHandler);

    }
  }
}
